if (!customElements.get('bb-scroll-carousel')) {
  customElements.define('bb-scroll-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.buttons = Array.from(this.querySelectorAll('[data-slide]'));
      this.controls = this.querySelector('[data-controls]');
      if (this.controls) this.controls.hidden = false;
      this.autoButton = this.querySelector('[data-autoplay-toggle]');
      this.autoEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.manualPaused = this.motion.matches || !!window.Shopify?.designMode;
      this.hovered = false;
      this.focusHeld = false;
      this.onScreen = false;
      this.holdUntil = 0;
      this.pointerHeld = false;
      this.ignoreHover = false;
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-autoplay-toggle')) {
          const resume = this.manualPaused || this.focusHeld || (this.hovered && !this.ignoreHover) || Date.now() < this.holdUntil;
          this.manualPaused = !resume;
          this.focusHeld = false;
          this.holdUntil = 0;
          this.ignoreHover = resume;
          this.schedule();
          return;
        }
        if (button.matches('[data-slide],[data-prev],[data-next]')) this.hold();
        if (button.hasAttribute('data-slide')) this.go(Number(button.dataset.slide));
        if (button.hasAttribute('data-prev')) this.go(this.index - 1);
        if (button.hasAttribute('data-next')) this.go(this.index + 1);
      }, options);
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.pause();
        this.go(event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { ...options, passive: true });
      this.track.addEventListener('pointerdown', event => {
        this.pointerOrigin = { x:event.clientX, y:event.clientY };
        this.pointerHeld = true;
        this.focusHeld = false;
        this.schedule();
      }, options);
      this.track.addEventListener('pointermove', event => {
        if (!this.pointerOrigin) return;
        const x = Math.abs(event.clientX - this.pointerOrigin.x);
        const y = Math.abs(event.clientY - this.pointerOrigin.y);
        if (x > 12 && x > y) this.hold();
      }, { ...options, passive:true });
      const releasePointer = () => { this.pointerOrigin = null; this.pointerHeld = false; this.schedule(); };
      window.addEventListener('pointerup', releasePointer, options);
      window.addEventListener('pointercancel', releasePointer, options);
      this.track.addEventListener('wheel', event => { if (Math.abs(event.deltaX) > Math.abs(event.deltaY) && Math.abs(event.deltaX) > 2) this.hold(); }, { ...options, passive: true });
      this.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); } }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.ignoreHover = false; this.schedule(); }, options);
      this.addEventListener('focusin', event => { if (!event.target.closest('[data-autoplay-toggle]') && event.target.matches(':focus-visible') && !this.pointerHeld) { this.focusHeld = true; this.schedule(); } }, options);
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) { this.focusHeld = false; this.schedule(); } }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', () => { if (this.motion.matches) this.manualPaused = true; this.schedule(); }, options);
      if (this.autoEnabled) {
        this.visibilityObserver = new IntersectionObserver(entries => {
          this.onScreen = entries.some(entry => entry.isIntersecting);
          this.schedule();
        }, { threshold: 0.2 });
        this.visibilityObserver.observe(this);
      }
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.update();
    }
    pause() {
      this.manualPaused = true;
      this.schedule();
    }
    hold() {
      this.holdUntil = Date.now() + 6000;
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.autoEnabled) return;
      const holdRemaining = Math.max(0, this.holdUntil - Date.now());
      const paused = this.manualPaused || this.focusHeld || this.pointerHeld || (this.hovered && !this.ignoreHover) || holdRemaining > 0;
      if (this.autoButton) {
        this.autoButton.setAttribute('aria-label', paused ? this.autoButton.dataset.playLabel : this.autoButton.dataset.pauseLabel);
        this.autoButton.dataset.state = paused ? 'paused' : 'playing';
      }
      const status = this.querySelector('[data-status]');
      if (status) status.setAttribute('aria-live', paused ? 'polite' : 'off');
      if (this.manualPaused || this.focusHeld || this.pointerHeld || (this.hovered && !this.ignoreHover) || !this.onScreen || document.hidden) return;
      if (holdRemaining > 0) {
        this.timer = setTimeout(() => {
          this.holdUntil = 0;
          this.go((this.index + 1) % this.slides.length);
          this.schedule();
        }, holdRemaining);
        return;
      }
      this.timer = setTimeout(() => {
        this.go((this.index + 1) % this.slides.length);
        this.schedule();
      }, Math.max(4000, Number(this.dataset.interval) || 6000));
    }
    go(index) {
      const slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
      if (!slide) return;
      const left = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
      this.track.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
    update() {
      const left = this.track.getBoundingClientRect().left;
      this.index = this.slides.reduce((best, slide, index) => Math.abs(slide.getBoundingClientRect().left - left) < Math.abs(this.slides[best].getBoundingClientRect().left - left) ? index : best, 0);
      this.buttons.forEach((button, index) => button.setAttribute('aria-pressed', String(index === this.index)));
      if (!this.hasAttribute('data-categories')) this.slides.forEach((slide, index) => { slide.inert = index !== this.index; });
      const previous = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (previous) previous.disabled = this.track.scrollLeft <= 2;
      if (next) next.disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      const status = this.querySelector('[data-status]');
      if (status) status.textContent = `${this.index + 1} / ${this.slides.length}`;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);
    }
  });
}
